"use client";

import {
  ContentGrid,
  EmptyState,
  Icon,
  PageHeader,
  StatusBadge,
  useAppContext,
} from "@repo/dashboard-core";
import { Alert, Loader } from "@mantine/core";
import { useTranslations } from "next-intl";
import { useCallback, useEffect, useState, useTransition } from "react";

import {
  fetchPhoneCheckPackages,
  fetchPhoneCheckUsage,
  runPhoneCheck,
} from "@/lib/actions/phone-checker";
import type {
  CheckInput,
  CheckUsage,
  PackageCatalogue,
  PhoneCheck,
} from "@/lib/types/phone-checker";
import { useServiceReview } from "@/lib/context/ServiceReviewContext";

import { CheckForm } from "./CheckForm";
import { CheckSummary } from "./CheckSummary";
import { PackageCard } from "./PackageCard";

/** Refusals the API names. Anything else falls back to the generic sentence. */
const KNOWN_ERRORS = new Set([
  "not-authenticated",
  "network-error",
  "subscription_required",
  "lookup-allowance-exceeded",
  "lookup-rate-limited",
  "lookup-provider-unavailable",
  "invalid-phone-number",
  "no-packages-requested",
]);

export function PhoneCheckerView() {
  const t = useTranslations("phone-checker");
  const { userInfo } = useAppContext();
  const [pending, startTransition] = useTransition();
  const askForReview = useServiceReview();

  const [catalogue, setCatalogue] = useState<PackageCatalogue | null>(null);
  const [usage, setUsage] = useState<CheckUsage | null>(null);
  const [check, setCheck] = useState<PhoneCheck | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [errorDetails, setErrorDetails] = useState<Record<
    string,
    unknown
  > | null>(null);
  const [loading, setLoading] = useState(true);

  const loadUsage = useCallback(async () => {
    const result = await fetchPhoneCheckUsage();
    if (result.success) setUsage(result.data);
  }, []);

  useEffect(() => {
    let active = true;

    (async () => {
      const [packages, usageResult] = await Promise.all([
        fetchPhoneCheckPackages(),
        fetchPhoneCheckUsage(),
      ]);

      if (!active) return;

      if (packages.success) setCatalogue(packages.data);
      else setError(packages.error);

      if (usageResult.success) setUsage(usageResult.data);
      setLoading(false);
    })();

    return () => {
      active = false;
    };
  }, []);

  const handleSubmit = (input: CheckInput) => {
    setError(null);
    setErrorDetails(null);

    startTransition(async () => {
      const result = await runPhoneCheck(input);

      if (result.success) {
        setCheck(result.data);
        // GD-120 — only a check that told the member something. An invalid
        // number, or a valid one whose checks all came back unavailable, is
        // still a success here, but not a moment to ask for a review. Not
        // "every check": Caller Name is on by default and Twilio never covers
        // it outside the US.
        const answered = result.data.packages.some(
          (pkg) => pkg.status === "ok",
        );
        if (result.data.valid && answered) askForReview("phone-checker");
        // The allowance moved, so the counter above the form has to move with
        // it — otherwise the customer only learns they are out by being
        // refused.
        await loadUsage();
        return;
      }

      setCheck(null);
      setError(result.error);
      setErrorDetails(result.details ?? null);
    });
  };

  const exhausted = usage ? usage.remaining <= 0 : false;

  if (loading) {
    return (
      <div className="flex h-80 items-center justify-center">
        <Loader size={48} variant="oval" color="var(--shell-primary)" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={t("title")}
        description={t("description")}
        actions={
          usage ? (
            <StatusBadge
              label={t("usage.remaining", {
                remaining: usage.remaining,
                limit: usage.limit,
              })}
              variant={
                usage.remaining <= 0
                  ? "error"
                  : usage.remaining <= 5
                    ? "warning"
                    : "neutral"
              }
            />
          ) : undefined
        }
      />

      {catalogue && !catalogue.configured && (
        <Alert color="yellow" radius="md" title={t("error.not-configured")}>
          {t("error.not-configured-description")}
        </Alert>
      )}

      {exhausted && (
        <Alert color="yellow" radius="md" title={t("usage.exhausted")}>
          {t("usage.exhausted-description", {
            date: usage
              ? new Date(usage.resetsAt).toLocaleDateString()
              : /* c8 ignore next */ "",
          })}
        </Alert>
      )}

      {error && (
        <Alert color="red" radius="md" title={t("error.title")}>
          {KNOWN_ERRORS.has(error)
            ? t(`error.${error}`, {
                used: Number(errorDetails?.used ?? 0),
                limit: Number(errorDetails?.limit ?? 0),
              })
            : t("error.generic")}
        </Alert>
      )}

      {catalogue && (
        <CheckForm
          catalogue={catalogue.packages}
          defaultCountry={userInfo.countryResidence ?? undefined}
          disabled={exhausted}
          pending={pending}
          onSubmit={handleSubmit}
        />
      )}

      {check ? (
        <div className="space-y-4">
          <CheckSummary check={check} />
          {/* No cards for a number Twilio judged invalid. Nothing was run and
              nothing could be, so the seven identical "not available" tiles
              under the red verdict would read as seven failures — the exact
              thing the verdict is there to prevent. */}
          {check.valid && (
            <ContentGrid cols={2}>
              {check.packages.map((result) => (
                <PackageCard key={result.package} result={result} />
              ))}
            </ContentGrid>
          )}
        </div>
      ) : (
        !pending && (
          <EmptyState
            icon={<Icon name="phoneCheck" size={48} />}
            title={t("empty.title")}
            description={t("empty.description")}
          />
        )
      )}
    </div>
  );
}
